import React, { useMemo } from 'react';
import { Stepper, Group, Text, Badge, Paper, Stack, Box } from '@mantine/core';
import {
  IconCheck,
  IconFileText,
  IconFlask,
  IconFileTypography,
  IconTrophy,
  IconSend,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { OrderSubState } from '../lims-types';
import { Consent, DiagnosticReport, ServiceRequest } from '@medplum/fhirtypes';
import {
  getOrderSubState,
  hasConsentAnswered,
} from '../utils/order-substates-helper';
import { useDateDisplay } from '@/hooks/useDateDisplay';

// -----------------------------------------------------------------------------
interface OrderSubStateStepperProps {
  serviceRequest: ServiceRequest;
  consent?: Consent;
  hasPayment?: boolean;
  consentDate?: string;
  specimenDate?: string;
  paymentDate?: string;
  latestReport?: DiagnosticReport;
}

// -----------------------------------------------------------------------------
type StepLabelKey =
  | 'orderSubStateStepper.steps.orderCreation'
  | 'orderSubStateStepper.steps.labProcess'
  | 'orderSubStateStepper.steps.report'
  | 'orderSubStateStepper.steps.followUp'
  | 'orderSubStateStepper.steps.completed';

type SubstepLabelKey =
  | 'orderSubStateStepper.substeps.consent'
  | 'orderSubStateStepper.substeps.specimen'
  | 'orderSubStateStepper.substeps.payment'
  | 'orderSubStateStepper.substeps.inProgress'
  | 'orderSubStateStepper.substeps.inReview';

interface StepGroupConfig {
  key: string;
  labelKey: StepLabelKey;
  icon: React.ReactNode;
  substates: OrderSubState[];
  entrySubstate: OrderSubState;
}

const STEP_GROUPS: StepGroupConfig[] = [
  {
    key: 'orderCreation',
    labelKey: 'orderSubStateStepper.steps.orderCreation',
    icon: <IconFileText size={16} />,
    substates: [OrderSubState.OrderCreated],
    entrySubstate: OrderSubState.OrderCreated,
  },
  {
    key: 'labProcess',
    labelKey: 'orderSubStateStepper.steps.labProcess',
    icon: <IconFlask size={16} />,
    substates: [OrderSubState.LabProcess],
    entrySubstate: OrderSubState.LabProcess,
  },
  {
    key: 'report',
    labelKey: 'orderSubStateStepper.steps.report',
    icon: <IconFileTypography size={16} />,
    substates: [OrderSubState.Report],
    entrySubstate: OrderSubState.Report,
  },
  {
    key: 'followUp',
    labelKey: 'orderSubStateStepper.steps.followUp',
    icon: <IconSend size={16} />,
    substates: [OrderSubState.FollowUpTasks],
    entrySubstate: OrderSubState.FollowUpTasks,
  },
  {
    key: 'completed',
    labelKey: 'orderSubStateStepper.steps.completed',
    icon: <IconTrophy size={16} />,
    substates: [OrderSubState.Completed],
    entrySubstate: OrderSubState.Completed,
  },
];

// -----------------------------------------------------------------------------
const getActiveIndex = (substate: OrderSubState | null): number => {
  // When fully completed, mark all steps as completed
  if (substate === OrderSubState.Completed) return STEP_GROUPS.length;
  if (!substate) return 0;
  const idx = STEP_GROUPS.findIndex((g) => g.substates.includes(substate));
  return idx >= 0 ? idx : 0;
};

// -----------------------------------------------------------------------------
const SubstepChip = ({
  label,
  completed,
  date,
  formatDate,
}: {
  label: string;
  completed: boolean;
  date?: string;
  formatDate: (date: string) => string;
}) => (
  <Paper
    py={4}
    px="sm"
    radius="xl"
    withBorder
    style={{
      borderColor: completed
        ? 'var(--mantine-color-teal-3)'
        : 'var(--mantine-color-gray-3)',
      backgroundColor: completed
        ? 'var(--mantine-color-teal-0)'
        : 'transparent',
    }}
  >
    <Group wrap="nowrap" justify="space-between">
      <Group wrap="nowrap" gap={6}>
        {completed ? (
          <IconCheck size={14} color="var(--mantine-color-teal-6)" />
        ) : (
          <Box
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: '2px solid var(--mantine-color-gray-4)',
              flexShrink: 0,
            }}
          />
        )}
        <Text size="xs" c={completed ? undefined : 'dimmed'}>
          {label}
        </Text>
      </Group>
      {completed && date && (
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {formatDate(date)}
        </Text>
      )}
    </Group>
  </Paper>
);

// -----------------------------------------------------------------------------
export const OrderSubStateStepper = ({
  serviceRequest,
  consent,
  hasPayment = false,
  consentDate,
  specimenDate,
  paymentDate,
  latestReport,
}: OrderSubStateStepperProps) => {
  const { t } = useTranslation('lims');
  const formatDate = useDateDisplay();
  const currentSubState = getOrderSubState(serviceRequest);
  const activeGroupIndex = getActiveIndex(currentSubState);

  // ---------------------------------------------------------------------------
  const getSubsteps = (
    groupKey: string
  ):
    | {
        key: string;
        labelKey: SubstepLabelKey;
        completed: boolean;
        date?: string;
      }[]
    | null => {
    if (groupKey === 'orderCreation') {
      return [
        {
          key: 'consent',
          labelKey: 'orderSubStateStepper.substeps.consent',
          completed: hasConsentAnswered(consent),
          date: consentDate,
        },
        {
          key: 'specimen',
          labelKey: 'orderSubStateStepper.substeps.specimen',
          completed: (serviceRequest.specimen?.length ?? 0) > 0,
          date: specimenDate,
        },
        {
          key: 'payment',
          labelKey: 'orderSubStateStepper.substeps.payment',
          completed: hasPayment,
          date: paymentDate,
        },
      ];
    }
    return null;
  };

  // Report status badge config
  const reportStatusBadge = useMemo(() => {
    if (!latestReport?.status) {
      return null;
    }
    switch (latestReport.status) {
      case 'registered':
        return {
          label: t('reportEditor.steps.inProgress'),
          color: 'blue',
        };
      case 'preliminary':
        return {
          label: t('reportEditor.steps.inReview'),
          color: 'orange',
        };
      case 'final':
        return {
          label: t('reportEditor.steps.approved'),
          color: 'green',
        };
      default:
        return null;
    }
  }, [latestReport?.status, t]);

  // ---------------------------------------------------------------------------
  return (
    <Paper p="md" withBorder>
      <Text size="sm" fw={600} mb="md">
        {t('orderSubStateStepper.title')}
      </Text>

      <Stepper
        active={activeGroupIndex}
        orientation="vertical"
        size="sm"
        styles={{
          stepLabel: { fontSize: '13px' },
          stepDescription: { maxWidth: '100%' },
          stepBody: { paddingTop: 6 },
        }}
      >
        {STEP_GROUPS.map((group, index) => {
          const isActive = index === activeGroupIndex;
          const substeps = getSubsteps(group.key);

          return (
            <Stepper.Step
              key={group.key}
              label={
                <Group gap="xs" align="center" wrap="nowrap">
                  <Text size="sm" fw={600}>
                    {t(group.labelKey)}
                  </Text>
                  {group.key === 'report' && reportStatusBadge && (
                    <Badge
                      size="xs"
                      color={reportStatusBadge.color}
                      variant="light"
                    >
                      {reportStatusBadge.label}
                    </Badge>
                  )}
                </Group>
              }
              description={
                <Stack gap={4}>
                  {isActive && (
                    <Badge
                      size="xs"
                      color="teal"
                      variant="filled"
                      w="fit-content"
                    >
                      {t('orderSubStateStepper.current')}
                    </Badge>
                  )}
                  {substeps && (
                    <Stack gap={4} mt={4}>
                      {substeps.map((sub) => (
                        <SubstepChip
                          key={sub.key}
                          label={t(sub.labelKey)}
                          completed={sub.completed}
                          date={sub.date}
                          formatDate={formatDate}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              }
              completedIcon={<IconCheck size={14} />}
              icon={group.icon}
              color={index < activeGroupIndex || isActive ? 'teal' : 'gray'}
            />
          );
        })}
      </Stepper>
    </Paper>
  );
};
